import { faEllipsisVertical, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { format, formatDistanceStrict } from 'date-fns';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseJsonSafe } from './ProcessingRecordModal';
import { getRecordStatusInfo } from './recordStatus.ts';
import DropdownMenu from '../../../../core/components/DropdownMenu';
import { withIcon } from '../../../../core/components/Icon';
import Table, { TableScrollWrapper } from '../../../../core/components/Table';
import TablePagination from '../../../../core/components/TablePagination';
import Tooltip from '../../../../core/components/Tooltip';
import classnames from '../../../../core/utils/classnames';

const EllipsisIcon = withIcon(faEllipsisVertical);
const LoadingIcon = withIcon(faSpinnerThird);

const ProcessingRecordRow = ({ record, onClick, onDelete, onRetry, onReprocess, onReuploadForTesting }: any) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const [showMenu, setShowMenu] = useState(false);

    const inputData = parseJsonSafe(record.inputData);

    const statusInfo = getRecordStatusInfo(record, t);
    const sender = inputData?.sender || '-';
    const subject = inputData?.subject || '-';
    const isFailed = record?.stepRun?.executionStatus === 'FAILED';
    const isSucceeded = record?.stepRun?.executionStatus === 'SUCCEEDED';

    const createdByName = record.createdBy
        ? `${record.createdBy.firstName || ''} ${record.createdBy.lastName || ''}`.trim()
        : '';

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <Table.Row className="cursor-pointer hover:bg-surface-weak transition-colors group" onClick={onClick}>
            <Table.Cell className="max-w-xs group-hover:bg-surface-strong transition-colors duration-200">
                <span className="truncate block">{sender}</span>
            </Table.Cell>

            <Table.Cell className="max-w-md group-hover:bg-surface-strong transition-colors duration-200">
                <span className="truncate block">{subject}</span>
            </Table.Cell>

            <Table.Cell className="whitespace-nowrap group-hover:bg-surface-strong transition-colors duration-200">
                {record.finishedAt ? (
                    <Tooltip
                        content={
                            <span className="whitespace-nowrap">
                                {t('historyPage.finishedAt')} {format(new Date(record.finishedAt), 'PPpp')}
                            </span>
                        }
                    >
                        <span>
                            {formatDistanceStrict(
                                new Date(record.stepRun?.createdAt || record.createdAt),
                                new Date(record.finishedAt)
                            )}
                        </span>
                    </Tooltip>
                ) : (
                    '-'
                )}
            </Table.Cell>

            <Table.Cell className="whitespace-nowrap group-hover:bg-surface-strong transition-colors duration-200">
                <span
                    className={classnames(
                        'inline-flex rounded text-xs font-medium items-center px-2 py-1 border border-solid',
                        statusInfo.colorClass
                    )}
                >
                    {statusInfo.label}
                </span>
            </Table.Cell>

            <Table.Cell className="whitespace-nowrap group-hover:bg-surface-strong transition-colors duration-200">
                {createdByName ? createdByName : '-'}
            </Table.Cell>

            <Table.Cell className="whitespace-nowrap group-hover:bg-surface-strong transition-colors duration-200">
                {format(new Date(record.createdAt), 'PPpp')}{' '}
            </Table.Cell>

            <Table.Cell
                className="whitespace-nowrap group-hover:bg-surface-strong transition-colors duration-200"
                onClick={handleMenuClick}
                sticky="right"
            >
                <DropdownMenu open={showMenu} onOpenChange={setShowMenu}>
                    <DropdownMenu.Trigger asChild>
                        <button
                            className={classnames(
                                'flex items-center justify-center gap-2 rounded transition-colors duration-200 hover:bg-surface-strong-hover active:bg-surface-strong-active w-8 h-8 cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong disabled:opacity-50 disabled:pointer-events-none',
                                showMenu && 'bg-surface-brand-medium-default text-content-brand-strong'
                            )}
                        >
                            <EllipsisIcon />
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        {isFailed && onRetry && (
                            <DropdownMenu.Item onClick={() => onRetry(record)}>
                                {t('historyPage.actions.retry')}
                            </DropdownMenu.Item>
                        )}
                        {isSucceeded && onReprocess && (
                            <DropdownMenu.Item onClick={() => onReprocess(record)}>
                                {t('historyPage.actions.reprocess')}
                            </DropdownMenu.Item>
                        )}
                        {onReuploadForTesting && (
                            <DropdownMenu.Item onClick={() => onReuploadForTesting(record)}>
                                {t('historyPage.actions.reuploadForTesting')}
                            </DropdownMenu.Item>
                        )}
                        {((isFailed && onRetry) || (isSucceeded && onReprocess) || onReuploadForTesting) && (
                            <DropdownMenu.Separator />
                        )}
                        <DropdownMenu.Item danger onClick={() => onDelete(record)}>
                            {t('historyPage.actions.delete')}
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu>
            </Table.Cell>
        </Table.Row>
    );
};

export interface ProcessingRecordsTableProps {
    records: any[];
    loading: boolean;
    totalCount: number;
    activePage: number;
    itemsPerPage: number;
    setActivePage: (page: number) => void;
    setItemsPerPage: (itemsPerPage: number) => void;
    onRowClick: (record: any) => void;
    onDelete: (record: any) => void;
    onRetry?: (record: any) => void;
    onReprocess?: (record: any) => void;
    onReuploadForTesting?: (record: any) => void;
}

// The records table body (columns, row, pagination, loading/empty states) shared
// between the standalone Agent history/testing pages and the Mailbox details
// "Agent activity" tab.
const ProcessingRecordsTable = ({
    records,
    loading,
    totalCount,
    activePage,
    itemsPerPage,
    setActivePage,
    setItemsPerPage,
    onRowClick,
    onDelete,
    onRetry,
    onReprocess,
    onReuploadForTesting,
}: ProcessingRecordsTableProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    return (
        <div className="flex flex-col gap-6">
            <TableScrollWrapper>
                <Table className={classnames('w-full table-auto', loading && 'opacity-50')}>
                    <Table.Head>
                        <Table.Row>
                            <Table.HeadCell className="min-w-[10rem]">{t('historyPage.table.sender')}</Table.HeadCell>
                            <Table.HeadCell className="min-w-[14rem]">{t('historyPage.table.subject')}</Table.HeadCell>
                            <Table.HeadCell>{t('historyPage.table.duration')}</Table.HeadCell>
                            <Table.HeadCell>{t('historyPage.table.status')}</Table.HeadCell>
                            <Table.HeadCell>{t('historyPage.table.createdBy')}</Table.HeadCell>
                            <Table.HeadCell>{t('historyPage.table.createdAt')}</Table.HeadCell>
                            <Table.HeadCell className="w-8" sticky="right" />
                        </Table.Row>
                    </Table.Head>
                    <Table.Body>
                        {records && records.length > 0 ? (
                            records.map((record: any) => (
                                <ProcessingRecordRow
                                    key={record.id}
                                    record={record}
                                    onClick={() => onRowClick(record)}
                                    onDelete={onDelete}
                                    onRetry={onRetry}
                                    onReprocess={onReprocess}
                                    onReuploadForTesting={onReuploadForTesting}
                                />
                            ))
                        ) : loading ? (
                            <Table.Row>
                                <Table.Cell colSpan={7}>
                                    <div className="py-5 flex justify-center">
                                        <LoadingIcon spin className="text-content-medium text-lg" />
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan={7}>
                                    <div className="py-5 flex flex-col gap-2">
                                        <div className="text-center font-medium text-content-medium">
                                            {t('historyPage.noData.title')}
                                        </div>
                                        <div className="text-center text-content-medium">
                                            {t('historyPage.noData.message')}
                                        </div>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </TableScrollWrapper>

            {totalCount > 0 && (
                <TablePagination
                    totalCount={totalCount}
                    activePage={activePage}
                    itemsPerPage={itemsPerPage}
                    setActivePage={setActivePage}
                    setItemsPerPage={setItemsPerPage}
                />
            )}
        </div>
    );
};

export default ProcessingRecordsTable;
